import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Globe } from '@digital-twins/design-system/icons'
import { COUNTRIES } from '@digital-twins/shared-types'
import type { LibraryFacetRuleField, LibraryRuleField, LibraryTopicBrandRule } from '@digital-twins/shared-types'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { IDLE_PILL_CLASS, SocialFilterToken } from '@/pages/KnowledgeHubPage/social/SocialFilterPill'
import { SocialFilterMenu } from '@/pages/KnowledgeHubPage/social/SocialFilterPopover'
import { marketFlagEmoji } from '@/pages/KnowledgeHubPage/libraryHelpers'
import type { LibraryFacetOption } from '@/pages/KnowledgeCollectionPage/useCollectionFilters'
import type { CollectionRuleDraft, InvalidRuleToken, TopicBrandOption } from './CollectionCreateDialog'
import { addedRuleTokens, COLLECTION_RULE_VALUES_MAX, topicBrandKey } from './collectionRuleValues'

export interface CollectionRuleFieldsProps {
  fields: Array<LibraryRuleField>
  draft: CollectionRuleDraft
  onChange: (draft: CollectionRuleDraft) => void
  optionsFor: (field: LibraryFacetRuleField) => Array<LibraryFacetOption>
  isLoadingOptions: boolean
  topicOptions: Array<TopicBrandOption>
  isLoadingTopics: boolean
  labelOf: (field: LibraryRuleField, token: string) => string | null
  invalidRule: InvalidRuleToken | null
}

/** Saved values stay removable even when the field no longer offers options. */
export function CollectionRuleFields(props: CollectionRuleFieldsProps) {
  const { fields, draft, onChange, optionsFor, isLoadingOptions, topicOptions, isLoadingTopics, labelOf, invalidRule } = props
  return (
    <div className="flex flex-col gap-4">
      {fields.map(field => {
        const error = invalidRule?.field === field ? invalidRule : null
        if (field === 'topicBrands') {
          if (!isLoadingTopics && topicOptions.length === 0 && draft.topicBrands.length === 0 && !error) return null
          return <TopicBrandRuleField key={field} selected={draft.topicBrands} options={topicOptions} isLoading={isLoadingTopics}
            labelOf={token => labelOf(field, token)} error={error} onChange={topicBrands => onChange({ ...draft, topicBrands })} />
        }
        const options = optionsFor(field)
        if (!isLoadingOptions && options.length === 0 && draft[field].length === 0 && !error) return null
        return <FacetRuleField key={field} field={field} selected={draft[field]} options={options} isLoading={isLoadingOptions}
          labelOf={token => labelOf(field, token)} error={error} onChange={tokens => onChange({ ...draft, [field]: tokens })} />
      })}
    </div>
  )
}

/** Flags are decoration for recognized country codes, never a market normalizer. */
export function CollectionMarketValue({ value }: { value: string }) {
  const country = COUNTRIES.find(country => country.code === value.toUpperCase())
  if (country) return <span className="inline-flex items-center gap-1.5"><span aria-hidden="true">{marketFlagEmoji(country.code)}</span><span>{value}</span></span>
  if (value === 'Global') return <span className="inline-flex items-center gap-1.5"><Globe className="size-3.5" aria-hidden="true" /><span>{value}</span></span>
  return <span>{value}</span>
}

function matchesQuery(text: string, query: string): boolean {
  return text.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
}

function RuleError({ field, error }: { field: LibraryRuleField; error: InvalidRuleToken | null }) {
  const { t } = useTranslation('app')
  return error ? <Text as="span" size="xs" className="text-destructive" role="alert" data-testid={`collection-dialog-field-${field}-error`}>{t('library.collections.dialog.valueUnavailable')}</Text> : null
}

interface FacetRuleFieldProps {
  field: LibraryFacetRuleField
  selected: Array<string>
  options: Array<LibraryFacetOption>
  isLoading: boolean
  labelOf: (token: string) => string | null
  error: InvalidRuleToken | null
  onChange: (tokens: Array<string>) => void
}

function FacetRuleField({ field, selected, options, isLoading, labelOf, error, onChange }: FacetRuleFieldProps) {
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const selectedSet = new Set(selected)
  const picked = options.filter(option => option.tokens.some(token => selectedSet.has(token)))
  const optionTokens = new Set(options.flatMap(option => option.tokens))
  const held = selected.filter(token => !optionTokens.has(token))
  const available = options.filter(option => !picked.includes(option) && matchesQuery(option.value, query))
  const testId = `collection-dialog-field-${field}`
  const label = t(`library.collections.dialog.pick.${field}`)
  const value = (text: string) => field === 'markets' ? <CollectionMarketValue value={text} /> : text
  const remove = (tokens: Array<string>) => onChange(selected.filter(token => !tokens.includes(token)))

  return <div className="flex min-w-0 flex-col gap-2.5" data-testid={testId} data-invalid={error ? 'true' : undefined}>
    <div className="flex flex-wrap items-center gap-2">
      <SocialFilterMenu label={label} selected={null} isOpen={open} onOpenChange={setOpen} isLoading={isLoading}
        search={{ value: query, onChange: setQuery, label: `${label}: ${t('library.collections.dialog.searchRules')}`, placeholder: t('library.collections.dialog.searchRules'), emptyMessage: t('library.collections.dialog.noOptions') }}
        footer={available.some(option => addedRuleTokens(selected, option.tokens) === null) ? t('library.collections.dialog.selectionLimit', { count: COLLECTION_RULE_VALUES_MAX }) : undefined}
        options={available.map(option => ({ value: option.value, label: value(option.value), testId: `${testId}-option-${option.value}`, isDisabled: addedRuleTokens(selected, option.tokens) === null }))}
        onSelect={displayValue => {
          const option = available.find(option => option.value === displayValue)
          if (!option) return
          const next = addedRuleTokens(selected, option.tokens)
          if (next) onChange(next)
        }}
        trigger={<button type="button" className={IDLE_PILL_CLASS} data-testid={`${testId}-trigger`}><span>{label}</span><ChevronDown className="size-3.5" aria-hidden="true" /></button>}
        testId={testId} />
      <div className="flex flex-wrap gap-2" data-testid={`${testId}-selected`}>
      {picked.map((option, index) => <SocialFilterToken key={option.value} value={value(option.value)}
        removeLabel={t('library.collections.resources.filters.remove', { value: option.value })} onRemove={() => remove(option.tokens)} testId={`${testId}-selected-${index}`} />)}
      {held.map(token => {
        const heldLabel = labelOf(token) ?? t('library.collections.dialog.valueUnavailable')
        return <SocialFilterToken key={token} value={<span className={cn(error?.token === token && 'text-destructive')}>{value(heldLabel)}</span>}
          removeLabel={t('library.collections.resources.filters.remove', { value: heldLabel })} onRemove={() => remove([token])} testId={`${testId}-held-${token}`} />
      })}
      </div>
    </div>
    <RuleError field={field} error={error} />
  </div>
}

interface TopicBrandRuleFieldProps {
  selected: Array<LibraryTopicBrandRule>
  options: Array<TopicBrandOption>
  isLoading: boolean
  labelOf: (token: string) => string | null
  error: InvalidRuleToken | null
  onChange: (pairs: Array<LibraryTopicBrandRule>) => void
}

function TopicBrandRuleField({ selected, options, isLoading, labelOf, error, onChange }: TopicBrandRuleFieldProps) {
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const selectedKeys = new Set(selected.map(topicBrandKey))
  const optionByKey = new Map(options.map(option => [topicBrandKey(option.pair), option]))
  const available = options.filter(option => !selectedKeys.has(topicBrandKey(option.pair)) && (matchesQuery(option.brandName, query) || matchesQuery(option.topicName, query)))
  const testId = 'collection-dialog-field-topicBrands'
  const label = t('library.collections.dialog.pick.topicBrands')
  return <div className="flex min-w-0 flex-col gap-2.5" data-testid={testId} data-invalid={error ? 'true' : undefined}>
    <div className="flex flex-wrap items-center gap-2">
      <SocialFilterMenu label={label} selected={null} isOpen={open} onOpenChange={setOpen} isLoading={isLoading}
        search={{ value: query, onChange: setQuery, label: `${label}: ${t('library.collections.dialog.searchRules')}`, placeholder: t('library.collections.dialog.searchRules'), emptyMessage: t('library.collections.dialog.noOptions') }}
        footer={selected.length >= COLLECTION_RULE_VALUES_MAX ? t('library.collections.dialog.selectionLimit', { count: COLLECTION_RULE_VALUES_MAX }) : undefined}
        options={available.map(option => ({ value: topicBrandKey(option.pair), label: <><span className="font-normal text-muted-foreground">{option.topicName}</span><span>{option.brandName}</span></>, testId: `${testId}-option-${topicBrandKey(option.pair)}`, isDisabled: selected.length >= COLLECTION_RULE_VALUES_MAX }))}
        onSelect={key => {
          const option = key == null ? undefined : optionByKey.get(key)
          if (option && !selectedKeys.has(key!) && selected.length < COLLECTION_RULE_VALUES_MAX) onChange([...selected, { ...option.pair }])
        }}
        trigger={<button type="button" className={IDLE_PILL_CLASS} data-testid={`${testId}-trigger`}><span>{label}</span><ChevronDown className="size-3.5" aria-hidden="true" /></button>}
        testId={testId} />
      <div className="flex flex-wrap gap-2" data-testid={`${testId}-selected`}>
      {selected.map((pair, index) => {
        const key = topicBrandKey(pair)
        const option = optionByKey.get(key)
        const brand = option?.brandName ?? labelOf(key) ?? t('library.collections.dialog.valueUnavailable')
        return <SocialFilterToken key={key} label={option?.topicName} value={<span className={cn(error?.token === key && 'text-destructive')}>{brand}</span>}
          removeLabel={t('library.collections.resources.filters.remove', { value: brand })}
          onRemove={() => onChange(selected.filter(pair => topicBrandKey(pair) !== key))} testId={`${testId}-selected-${index}`} />
      })}
      </div>
    </div>
    <RuleError field="topicBrands" error={error} />
  </div>
}
